/**
 * Bundle Builder — bundle-builder.js
 *
 * Vanilla JS, no dependencies, no build step.
 * One BundleBuilder instance per section on the page.
 *
 * Architecture:
 *   BundleBuilder
 *     ├─ BundleState   (Map of selected items, price maths)
 *     ├─ BundleTray    (sidebar/drawer UI)
 *     └─ BundleCart    (AJAX /cart/add.js submission)
 */

/* ──────────────────────────────────────────────
   Utilities
────────────────────────────────────────────── */

/**
 * Format a Shopify price integer (cents) into a money string.
 * Respects the shop's money_format passed via __bb_config.
 */
function bbFormatMoney(cents, moneyFormat) {
  if (!cents && cents !== 0) return '—';
  const amount = (cents / 100).toFixed(2);
  // Handle common Shopify money format tokens
  return moneyFormat
    .replace('{{amount}}', amount)
    .replace('{{amount_no_decimals}}', Math.round(cents / 100))
    .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
    .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100))
    // Fallback: if format is something like "${{amount}}", replace the token
    .replace(/\{\{.*?\}\}/, amount);
}

/** Line item properties for /cart/add.js must be string values. */
/** Zepto sometimes JSON-stringifies `properties` on the wire. */
function bbCoercePropertiesObject(props) {
  if (props == null || props === '') return null;
  if (typeof props === 'string') {
    try {
      const p = JSON.parse(props);
      return typeof p === 'object' && p && !Array.isArray(p) ? p : null;
    } catch (_) {
      return null;
    }
  }
  if (typeof props === 'object' && !Array.isArray(props)) return props;
  return null;
}

function bbClonePlainObject(obj) {
  if (!obj || typeof obj !== 'object') return null;
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch (_) {
    return { ...obj };
  }
}

/** Read Shopify `properties[Label]` pairs from DOM (Zepto PDP / popup forms). */
function bbInspectFormPropertyPairs(formEl) {
  if (!formEl || typeof formEl.querySelectorAll !== 'function') return null;
  const out = {};
  for (const el of formEl.querySelectorAll('[name^="properties["]')) {
    if (el.disabled) continue;
    const nm = el.getAttribute('name');
    if (!nm) continue;
    const m = nm.match(/^properties\[(.+)]$/);
    if (!m) continue;
    const label = m[1];
    const t = String(el.type || '').toLowerCase();
    if (t === 'file') continue;
    if (t === 'checkbox') {
      if (!el.checked) continue;
      out[label] = el.value !== '' ? String(el.value) : 'Yes';
      continue;
    }
    if (t === 'radio') {
      if (!el.checked) continue;
      out[label] = String(el.value != null ? el.value : '');
      continue;
    }

    let val =
      typeof el.value !== 'undefined' && el.value != null ? String(el.value) : '';

    /** `select-multiple`: join selected values */
    if ((el.tagName || '').toUpperCase() === 'SELECT') {
      const sel = el;
      if (sel.multiple) {
        const picked = [...sel.selectedOptions].map(o => o.value || o.textContent);
        if (!picked.length) continue;
        val = picked.join(', ');
      }
    }
    out[label] = val;
  }
  return Object.keys(out).length ? bbClonePlainObject(out) : null;
}

/**
 * Turn Zepto / Shopify line `properties` into a plain key/value object
 * (string, JSON string, or [{ name, value }, …] arrays).
 */
function bbExtractLineProperties(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') {
    const parsed = bbCoercePropertiesObject(raw);
    return parsed && Object.keys(parsed).length ? bbClonePlainObject(parsed) : null;
  }
  if (Array.isArray(raw)) {
    const out = {};
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') continue;
      const name = entry.name ?? entry.Name ?? entry.property ?? entry.key;
      if (name == null) continue;
      const val =
        entry.value != null
          ? entry.value
          : entry.val != null
            ? entry.val
            : entry.values != null
              ? entry.values
              : '';
      out[String(name)] =
        typeof val === 'object' && val !== null && !(val instanceof File)
          ? JSON.stringify(val)
          : val;
    }
    return Object.keys(out).length ? bbClonePlainObject(out) : null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return Object.keys(raw).length ? bbClonePlainObject(raw) : null;
  }
  return null;
}

function bbSanitizeLineProperties(props) {
  if (!props || typeof props !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(props)) {
    if (v == null) continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = String(v);
    } else if (typeof File !== 'undefined' && v instanceof File) {
      continue;
    } else if (typeof Blob !== 'undefined' && v instanceof Blob) {
      continue;
    } else if (typeof v === 'object') {
      try {
        out[k] = JSON.stringify(v);
      } catch (_) {
        out[k] = String(v);
      }
    }
  }
  return out;
}

const BB_SESSION_PREFIX = 'bb-bundle-v1:';

function bbBundleSessionSave(sectionId, rows) {
  try {
    sessionStorage.setItem(BB_SESSION_PREFIX + sectionId, JSON.stringify({ items: rows }));
  } catch (_) {}
}

function bbBundleSessionLoad(sectionId) {
  try {
    const raw = sessionStorage.getItem(BB_SESSION_PREFIX + sectionId);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data.items) ? data.items : null;
  } catch (_) {
    return null;
  }
}

function bbBundleSessionClear(sectionId) {
  try {
    sessionStorage.removeItem(BB_SESSION_PREFIX + sectionId);
  } catch (_) {}
}

function bbQuery(selector, ctx) {
  return (ctx || document).querySelector(selector);
}

function bbQueryAll(selector, ctx) {
  return Array.from((ctx || document).querySelectorAll(selector));
}

/**
 * Notify the theme to refresh cart UI (bubble count, quick cart, etc).
 * This theme uses a global event bus (`r$1`) rather than DOM events.
 * Keep DOM CustomEvent fallback for safety / future portability.
 */
async function bbNotifyCartUpdated() {
  let cart = null;
  try {
    const res = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) cart = await res.json();
  } catch (_) {}

  // Update any cart count bubbles we can find (theme + common fallbacks)
  try {
    if (cart && typeof cart.item_count !== 'undefined') {
      const nodes = [
        ...document.querySelectorAll('[data-cart-count]'),
        ...document.querySelectorAll('.site-header__cart-count'),
        ...document.querySelectorAll('.cart-count'),
      ];
      nodes.forEach(el => {
        try {
          el.textContent = String(cart.item_count);
        } catch (_) {}
      });
    }
  } catch (_) {}

  // Theme event bus (assets/theme.js)
  try {
    if (typeof window.r$1 === 'function') {
      if (cart) window.r$1('cart:updated', { cart });
      window.r$1('quick-cart:updated');
    }
  } catch (_) {}

  // DOM event fallback (if custom-events.js / other scripts listen)
  try {
    // This theme's quick cart listens for this and then runs refreshQuickCart()
    document.dispatchEvent(
      new CustomEvent('apps:product-added-to-cart', {
        bubbles: true,
        detail: cart ? { cart } : {},
      })
    );

    if (cart) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
      );
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('quick-cart:updated', { bubbles: true }));
  } catch (_) {}
}

function bbOpenQuickCart() {
  try {
    const wrapper = document.querySelector('.quick-cart__wrapper');
    if (!wrapper) return false;
    wrapper.classList.add('active');
    document.body.setAttribute('data-fluorescent-overlay-open', 'true');
    return true;
  } catch (_) {
    return false;
  }
}

/* ──────────────────────────────────────────────
   BundleState
   Holds the selected items as a Map:
     variantId (number) → {
       variantId, productId, title, variantTitle,
       price (cents), image, quantity
     }
────────────────────────────────────────────── */

class BundleState {
  constructor({ minItems, maxItems, discountPct }) {
    this.minItems = minItems;
    this.maxItems = maxItems;
    this.discountPct = discountPct;
    this._items = new Map();
    this._listeners = [];
  }

  /** Total number of individual product items selected */
  get count() {
    let n = 0;
    this._items.forEach(item => (n += item.quantity));
    return n;
  }

  /**
   * Count used by the minimum-items unlock rule.
   * Ignore $0 personalized helper lines (e.g. personalize add-on rows).
   */
  get qualifyingCount() {
    let n = 0;
    this._items.forEach(item => {
      const isZeroPersonalizeHelper =
        !!item.personalized && Number(item.price) <= 0;
      if (!isZeroPersonalizeHelper) n += item.quantity;
    });
    return n;
  }

  get items() {
    return Array.from(this._items.values());
  }

  get isReady() {
    return this.qualifyingCount >= this.minItems;
  }

  get isMaxed() {
    return this.count >= this.maxItems;
  }

  /** Total price of selected items in cents (before discount) */
  get subtotalCents() {
    let total = 0;
    this._items.forEach(item => (total += item.price * item.quantity));
    return total;
  }

  /** Discount amount in cents */
  get discountCents() {
    return Math.round(this.subtotalCents * (this.discountPct / 100));
  }

  /** Final bundle price in cents */
  get totalCents() {
    return this.subtotalCents - this.discountCents;
  }

  /**
   * True if this variant appears anywhere in the bundle (standard line or
   * a personalized / add-on line). Used for card selection + Personalize
   * button state — a personalized-only row must still count as “in bundle”.
   */
  hasVariant(variantId) {
    const vid = Number(variantId);
    for (const item of this._items.values()) {
      if (Number(item.variantId) === vid) return true;
    }
    return false;
  }

  /**
   * True if the bundle contains the card’s storefront variant via a plain
   * line OR a personalized line anchored to this parent variant + product.
   * Used so “personalize only” picks still count toward max slots + selection.
   */
  includesCardVariant(productId, variantId) {
    const vid = Number(variantId);
    if (Number.isNaN(vid)) return false;
    const pidRaw =
      productId != null && productId !== ''
        ? Number(productId)
        : null;
    const pid =
      pidRaw != null && !Number.isNaN(pidRaw) ? pidRaw : null;
    for (const item of this._items.values()) {
      if (Number(item.variantId) === vid) return true;
      if (
        item.personalized &&
        item.personalizationParentVariantId != null &&
        Number(item.personalizationParentVariantId) === vid &&
        (pid == null ||
          item.personalizationParentProductId == null ||
          Number(item.personalizationParentProductId) === pid)
      )
        return true;
    }
    return false;
  }

  /**
   * Find a non-personalized line for the given variant (used for the
   * standard "Add to Bundle" toggle behaviour). Personalized lines are
   * always kept as separate entries so they don't collapse together.
   */
  _findStandardLine(variantId) {
    const vid = Number(variantId);
    for (const [key, item] of this._items.entries()) {
      if (Number(item.variantId) === vid && !item.personalized) return key;
    }
    return null;
  }

  /**
   * Drop the plain “Add to Bundle” row only (no linked add-on cleanup).
   * @returns {string[]} Shopify cart line keys removed from bundle state (if any).
   */
  removeStandardLineOnly(variantId) {
    const key = this._findStandardLine(Number(variantId));
    if (key == null) return [];
    const item = this._items.get(key);
    const cartKeys = item && item.cartLineKey ? [item.cartLineKey] : [];
    this._items.delete(key);
    this._emit();
    return cartKeys;
  }

  /**
   * Remove personalized rows (main + Item Personalization fee, etc.) tied
   * to a parent variant before applying a fresh Zepto batch.
   */
  removePersonalizedLinesForParent(parentVariantId, parentProductId) {
    const pv = Number(parentVariantId);
    if (Number.isNaN(pv)) return [];
    const ppRaw =
      parentProductId != null && parentProductId !== ''
        ? Number(parentProductId)
        : null;
    const pp = ppRaw != null && !Number.isNaN(ppRaw) ? ppRaw : null;
    let changed = false;
    /** @type {string[]} */
    const cartKeys = [];
    for (const [key, item] of [...this._items.entries()]) {
      if (!item.personalized) continue;
      if (item.personalizationParentVariantId == null) continue;
      if (Number(item.personalizationParentVariantId) !== pv) continue;
      if (
        pp != null &&
        item.personalizationParentProductId != null &&
        Number(item.personalizationParentProductId) !== pp
      ) {
        continue;
      }
      if (item.cartLineKey) cartKeys.push(item.cartLineKey);
      this._items.delete(key);
      changed = true;
    }
    if (changed) this._emit();
    return cartKeys;
  }

  /**
   * Update the main personalized line's properties for a parent variant.
   * Used when Zepto finalizes attrs via /cart/change after /cart/add.
   */
  patchMainPersonalizedProperties(parentVariantId, parentProductId, properties) {
    const pv = Number(parentVariantId);
    if (Number.isNaN(pv) || !properties || typeof properties !== 'object') return;
    const pp =
      parentProductId != null && parentProductId !== '' && !Number.isNaN(Number(parentProductId))
        ? Number(parentProductId)
        : null;
    let changed = false;
    for (const item of this._items.values()) {
      if (!item.personalized) continue;
      if (Number(item.variantId) !== pv) continue;
      if (
        pp != null &&
        item.personalizationParentProductId != null &&
        Number(item.personalizationParentProductId) !== pp
      ) {
        continue;
      }
      item.properties = { ...(item.properties || {}), ...bbClonePlainObject(properties) };
      changed = true;
    }
    if (changed) this._emit();
  }

  /**
   * When the base "Add to Bundle" line is removed, delete personalized
   * lines that were tagged as belonging to that product + variant
   * (`personalizationParentProductId` / `personalizationParentVariantId`).
   * Falls back to matching `variantId` only for legacy rows without tags.
   */
  /**
   * @returns {string[]} cart line keys removed alongside personalized rows.
   */
  _deletePersonalizedLinesForStandardRemoval(standardItem) {
    const sv = Number(standardItem.variantId);
    const sp =
      standardItem.productId != null && standardItem.productId !== ''
        ? Number(standardItem.productId)
        : null;

    /** @type {string[]} */
    const cartKeys = [];
    for (const [key, item] of [...this._items.entries()]) {
      if (!item.personalized) continue;

      const pv = item.personalizationParentVariantId;
      const pp = item.personalizationParentProductId;

      if (pv != null) {
        if (Number(pv) !== sv) continue;
        if (pp != null && sp != null && Number(pp) !== sp) continue;
        if (item.cartLineKey) cartKeys.push(item.cartLineKey);
        this._items.delete(key);
        continue;
      }

      if (Number(item.variantId) === sv) {
        if (item.cartLineKey) cartKeys.push(item.cartLineKey);
        this._items.delete(key);
      }
    }
    return cartKeys;
  }

  /**
   * Add an item to the bundle. Personalized items always go in as new
   * lines so each personalization is preserved. Non-personalized adds of
   * the same variant collapse into a single quantity-incrementing line.
   */
  /**
   * @returns {string|number|null} internal bundle lineKey, or null if maxed out.
   */
  add(item) {
    if (this.count >= this.maxItems) return null;

    const isPersonalized = !!item.personalized;
    if (!isPersonalized) {
      const existingKey = this._findStandardLine(item.variantId);
      if (existingKey) {
        this._items.get(existingKey).quantity += 1;
        this._emit();
        return existingKey;
      }
    }

    const lineKey = isPersonalized
      ? `pp-${item.variantId}-${Date.now()}-${Math.floor(Math.random() * 9999)}`
      : item.variantId;

    this._items.set(lineKey, {
      ...item,
      quantity: 1,
      lineKey,
      personalized: isPersonalized,
      properties: item.properties || null,
    });
    this._emit();
    return lineKey;
  }

  remove(lineKey) {
    const item = this._items.get(lineKey);
    if (!item) return;
    if (item.quantity > 1) {
      item.quantity -= 1;
    } else {
      this._items.delete(lineKey);
    }
    this._emit();
  }

  /**
   * @returns {string[]} Shopify cart `key` strings for rows that were tracked in cart.
   */
  removeAll(lineKey) {
    /** @type {string[]} */
    const cartKeys = [];
    let mapKey = lineKey;
    if (!this._items.has(mapKey) && typeof lineKey === 'string' && /^\d+$/.test(lineKey)) {
      const n = Number(lineKey);
      if (this._items.has(n)) mapKey = n;
    }

    if (this._items.has(mapKey)) {
      const item = this._items.get(mapKey);
      const wasStandard = !item.personalized;
      const removedSnapshot = wasStandard
        ? {
            variantId: item.variantId,
            productId: item.productId,
          }
        : null;
      if (item.cartLineKey) cartKeys.push(item.cartLineKey);
      this._items.delete(mapKey);
      if (wasStandard && removedSnapshot) {
        cartKeys.push(
          ...this._deletePersonalizedLinesForStandardRemoval(removedSnapshot)
        );
      }
      this._emit();
      return cartKeys;
    }

    const variantIdForLookup =
      typeof lineKey === 'number'
        ? lineKey
        : typeof lineKey === 'string' && /^\d+$/.test(lineKey)
          ? Number(lineKey)
          : lineKey;

    const standardKey = this._findStandardLine(variantIdForLookup);
    if (standardKey != null) {
      const item = this._items.get(standardKey);
      const removedSnapshot = {
        variantId: item.variantId,
        productId: item.productId,
      };
      if (item.cartLineKey) cartKeys.push(item.cartLineKey);
      this._items.delete(standardKey);
      cartKeys.push(
        ...this._deletePersonalizedLinesForStandardRemoval(removedSnapshot)
      );
      this._emit();
      return cartKeys;
    }

    /**
     * Card “toggle off” passes numeric variant id. If the customer already
     * personalized, there is no standard row — remove the main personalized
     * line(s) and any add-on lines (e.g. Item Personalization) tagged to it.
     */
    const vid =
      typeof variantIdForLookup === 'number'
        ? variantIdForLookup
        : typeof variantIdForLookup === 'string' && /^\d+$/.test(variantIdForLookup)
          ? Number(variantIdForLookup)
          : NaN;
    if (!Number.isNaN(vid)) {
      let changed = false;
      for (const [key, item] of [...this._items.entries()]) {
        if (Number(item.variantId) === vid) {
          if (item.cartLineKey) cartKeys.push(item.cartLineKey);
          this._items.delete(key);
          changed = true;
          continue;
        }
        if (
          item.personalized &&
          item.personalizationParentVariantId != null &&
          Number(item.personalizationParentVariantId) === vid
        ) {
          if (item.cartLineKey) cartKeys.push(item.cartLineKey);
          this._items.delete(key);
          changed = true;
        }
      }
      if (changed) this._emit();
    }
    return cartKeys;
  }

  onChange(fn) {
    this._listeners.push(fn);
  }

  clear() {
    this._items.clear();
    this._emit();
  }

  touch() {
    this._emit();
  }

  _emit() {
    this._listeners.forEach(fn => fn(this));
  }

  /** Snapshot for sessionStorage (restore after refresh). */
  serializeForSession() {
    return this.items.map(i => {
      const row = {
        lineKey: i.lineKey,
        variantId: i.variantId,
        quantity: i.quantity,
        price: i.price,
        title: i.title,
        variantTitle: i.variantTitle,
        image: i.image,
        productId: i.productId,
        personalized: !!i.personalized,
        syncedToCart: !!i.syncedToCart,
        properties: i.properties,
        personalizationParentProductId: i.personalizationParentProductId,
        personalizationParentVariantId: i.personalizationParentVariantId,
      };
      if (i.cartLineKey) row.cartLineKey = i.cartLineKey;
      return row;
    });
  }

  /**
   * Restore tray contents from session (same tab). Bypasses add() limits;
   * caller should only pass trusted serialized data from this shop.
   */
  hydrateFromSession(rows) {
    if (!Array.isArray(rows) || !rows.length) return;
    this._items.clear();
    for (const row of rows) {
      const vid = Number(row.variantId);
      if (!vid) continue;
      const lineKey =
        row.lineKey != null && row.lineKey !== ''
          ? String(row.lineKey)
          : String(vid);
      this._items.set(lineKey, {
        variantId: vid,
        quantity: Math.max(1, Number(row.quantity) || 1),
        price: Number(row.price) || 0,
        title: row.title || 'Product',
        variantTitle: row.variantTitle || 'Default Title',
        image: row.image || '',
        productId:
          row.productId != null && row.productId !== ''
            ? Number(row.productId)
            : null,
        personalized: !!row.personalized,
        syncedToCart: !!row.syncedToCart,
        ...(row.cartLineKey ? { cartLineKey: String(row.cartLineKey) } : {}),
        properties:
          row.properties && typeof row.properties === 'object'
            ? row.properties
            : null,
        personalizationParentProductId:
          row.personalizationParentProductId != null &&
          row.personalizationParentProductId !== ''
            ? Number(row.personalizationParentProductId)
            : null,
        personalizationParentVariantId:
          row.personalizationParentVariantId != null &&
          row.personalizationParentVariantId !== ''
            ? Number(row.personalizationParentVariantId)
            : null,
        lineKey,
      });
    }
    this._emit();
  }
}

/* ──────────────────────────────────────────────
   BundleTray
   Owns all DOM updates for the sidebar/drawer.
────────────────────────────────────────────── */

class BundleTray {
  constructor(root, state, config) {
    this.root = root;
    this.state = state;
    this.config = config;

    this.tray       = bbQuery('.bb-tray', root);
    this.toggle     = bbQuery('.bb-tray__toggle', root);
    this.body       = bbQuery('.bb-tray__body', root);
    this.emptyEl    = bbQuery('.bb-tray__empty', root);
    this.itemsList  = bbQuery('.bb-tray__items', root);
    this.progressBar= bbQuery('.bb-progress__bar', root);
    this.progressLbl= bbQuery('.bb-progress__label', root);
    this.toggleCnt  = bbQuery('.bb-tray__toggle-count', root);
    this.summaryOrig= bbQuery('.bb-summary-original', root);
    this.summaryDisc= bbQuery('.bb-summary-discount', root);
    this.summaryTotl= bbQuery('.bb-summary-total', root);
    this.ctaBtn     = bbQuery('.bb-tray__cta', root);
    this.ctaRemain  = bbQuery('.bb-cta-remaining', root);
    this.maxNote    = bbQuery('.bb-tray__max-note', root);
    this.toast      = bbQuery('.bb-toast', root);
    this._toastTimer= null;

    this._bindToggle();
  }

  _bindToggle() {
    if (!this.toggle) return;
    this.toggle.addEventListener('click', () => {
      const open = !this.tray.classList.contains('is-open');
      this.tray.classList.toggle('is-open', open);
      this.toggle.setAttribute('aria-expanded', String(open));
    });
  }

  update(state) {
    const {
      count,
      qualifyingCount,
      minItems,
      maxItems,
      discountPct,
      subtotalCents,
      discountCents,
      totalCents,
      isReady,
      isMaxed,
      items
    } = state;

    // Progress bar
    const progress = Math.min(qualifyingCount / minItems, 1) * 100;
    if (this.progressBar) {
      this.progressBar.style.width = progress + '%';
      this.progressBar.setAttribute('aria-valuenow', qualifyingCount);
    }
    if (this.progressLbl) {
      if (isReady) {
        this.progressLbl.innerHTML = `<strong>Bundle unlocked!</strong> ${qualifyingCount} item${qualifyingCount !== 1 ? 's' : ''} selected.`;
      } else {
        const needed = minItems - qualifyingCount;
        this.progressLbl.innerHTML = `Select <strong>${needed}</strong> more item${needed !== 1 ? 's' : ''} to unlock your bundle deal`;
      }
    }

    // Toggle count badge
    if (this.toggleCnt) {
      this.toggleCnt.textContent = count;
      this.toggleCnt.setAttribute('aria-label', `${count} item${count !== 1 ? 's' : ''} selected`);
    }

    // Empty state
    const isEmpty = items.length === 0;
    if (this.emptyEl) this.emptyEl.style.display = isEmpty ? '' : 'none';
    if (this.itemsList) this.itemsList.style.display = isEmpty ? 'none' : '';

    // Render items
    this._renderItems(items, state);

    // Summary
    if (this.summaryOrig) {
      this.summaryOrig.textContent = items.length ? bbFormatMoney(subtotalCents, this.config.moneyFormat) : '—';
    }
    if (this.summaryDisc) {
      this.summaryDisc.textContent = items.length && discountPct > 0
        ? `- ${bbFormatMoney(discountCents, this.config.moneyFormat)}`
        : '—';
    }
    if (this.summaryTotl) {
      this.summaryTotl.textContent = items.length ? bbFormatMoney(totalCents, this.config.moneyFormat) : '—';
    }

    // CTA
    if (this.ctaBtn) {
      const ready = isReady;
      this.ctaBtn.disabled = !ready;
      this.ctaBtn.setAttribute('aria-disabled', String(!ready));
      this.ctaBtn.classList.toggle('is-ready', ready);
    }
    if (this.ctaRemain) {
      const needed = Math.max(minItems - qualifyingCount, 0);
      this.ctaRemain.textContent = needed;
    }

    // Max note
    if (this.maxNote) {
      if (isMaxed) {
        this.maxNote.textContent = `Maximum of ${maxItems} items reached.`;
        this.maxNote.style.display = '';
      } else {
        this.maxNote.style.display = 'none';
      }
    }

    // Disable non-selected cards if maxed
    bbQueryAll('.bb-card', this.root).forEach(card => {
      const productId = card.dataset.productId;
      const variantSelect = bbQuery('.bb-card__variant-select', card);
      const variantId = variantSelect ? Number(variantSelect.value) : null;
      const pid = productId != null ? String(productId) : null;
      const isSelected =
        variantId && state.includesCardVariant(pid, variantId);
      card.classList.toggle('is-max-disabled', isMaxed && !isSelected);
    });
  }

  _renderItems(items, state) {
    if (!this.itemsList) return;

    const liveKeys = new Set(items.map(i => i.lineKey));

    bbQueryAll('.bb-tray-item', this.itemsList).forEach(el => {
      if (!liveKeys.has(el.dataset.lineKey)) el.remove();
    });

    items.forEach(item => {
      const existing = bbQuery(
        `[data-line-key="${item.lineKey}"]`,
        this.itemsList
      );
      if (existing) {
        const qtyEl = bbQuery('.bb-tray-item__qty', existing);
        const priceEl = bbQuery('.bb-tray-item__price', existing);
        if (qtyEl) qtyEl.textContent = `× ${item.quantity}`;
        if (priceEl) priceEl.textContent = bbFormatMoney(item.price * item.quantity, this.config.moneyFormat);
      } else {
        this.itemsList.appendChild(this._createItemEl(item));
      }
    });
  }

  _createItemEl(item) {
    const li = document.createElement('li');
    li.className = 'bb-tray-item';
    li.dataset.lineKey = item.lineKey;
    li.dataset.variantId = item.variantId;
    if (item.personalized) li.classList.add('bb-tray-item--personalized');

    const imageHtml = item.image
      ? `<img class="bb-tray-item__image" src="${item.image}" alt="${this._esc(item.title)}" loading="lazy" width="48" height="48">`
      : `<div class="bb-tray-item__image--placeholder">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20"><path d="M7.5 6.75V6a4.5 4.5 0 119 0v.75m-9 0h9m-9 0H6A2.25 2.25 0 003.75 9v10.5A2.25 2.25 0 006 21.75h12A2.25 2.25 0 0020.25 19.5V9A2.25 2.25 0 0018 6.75h-1.5"/></svg>
         </div>`;

    const variantLine = item.variantTitle && item.variantTitle !== 'Default Title'
      ? `<p class="bb-tray-item__variant">${this._esc(item.variantTitle)}</p>`
      : '';

    const personalizedBadge = item.personalized
      ? `<span class="bb-tray-item__badge" aria-label="Personalized">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true">
             <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z"/>
           </svg>
           Personalized
         </span>`
      : '';

    const propsSummary = this._buildPropsSummary(item.properties);

    li.innerHTML = `
      ${imageHtml}
      <div class="bb-tray-item__info">
        <p class="bb-tray-item__title">${this._esc(item.title)} ${personalizedBadge}</p>
        ${variantLine}
        ${propsSummary}
        <p class="bb-tray-item__qty">× ${item.quantity}</p>
      </div>
      <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px">
        <span class="bb-tray-item__price">${bbFormatMoney(item.price * item.quantity, this.config.moneyFormat)}</span>
        <button type="button" class="bb-tray-item__remove" aria-label="Remove ${this._esc(item.title)} from bundle" data-line-key="${this._esc(item.lineKey)}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true">
            <path fill-rule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    `;
    return li;
  }

  /**
   * Build a tiny summary of the customer-visible personalization
   * properties (skipping the underscore-prefixed private ones Shopify
   * uses internally).
   */
  _buildPropsSummary(properties) {
    if (!properties) return '';
    const visible = Object.entries(properties).filter(
      ([k, v]) => k && k.charAt(0) !== '_' && v !== '' && v != null
    );
    if (!visible.length) return '';

    const parts = visible.slice(0, 3).map(([k, v]) => {
      const value = String(v).length > 28 ? String(v).slice(0, 28) + '…' : v;
      return `<span><strong>${this._esc(k)}:</strong> ${this._esc(value)}</span>`;
    });
    if (visible.length > 3) {
      parts.push(`<span>+${visible.length - 3} more</span>`);
    }
    return `<div class="bb-tray-item__props">${parts.join('')}</div>`;
  }

  setCtaLoading(loading) {
    if (!this.ctaBtn) return;
    this.ctaBtn.classList.toggle('is-loading', loading);
    this.ctaBtn.disabled = loading;
  }

  showToast(message, isError = false) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.style.background = isError ? 'var(--bb-discount)' : 'var(--bb-success, #2e7d32)';
    this.toast.classList.add('is-visible');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this.toast.classList.remove('is-visible');
    }, 3500);
  }

  _esc(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

/* ──────────────────────────────────────────────
   BundleCart
   Handles AJAX submission to /cart/add.js
────────────────────────────────────────────── */

class BundleCart {
  /**
   * Add each selected product to the cart as its own line item,
   * tagged with a shared _bundle_id, then automatically apply the
   * configured discount code so the bundle discount is on the cart.
   *
   * Why this approach:
   *   - Shopify's storefront Cart API does NOT allow custom line item
   *     prices (security restriction).
   *   - Adding products at their real prices + auto-applying a
   *     pre-created discount code is the only reliable no-app solution.
   *   - Merchant creates the discount code ONCE in Shopify admin and
   *     pastes the code into the section settings.
   */
  static async addToCart({ state, discountPct, discountCode }) {
    const bundleId = `bundle-${Date.now()}-${Math.floor(Math.random() * 9999)}`;
    const bundleLabel = discountPct > 0
      ? `${discountPct}% Bundle Deal`
      : 'Bundle';

    const pendingItems = state.items.filter(item => !item.syncedToCart);
    if (!pendingItems.length) {
      return { ok: true, skipped: true };
    }

    const items = pendingItems.map((item, idx) => ({
      id: item.variantId,
      quantity: item.quantity,
      properties: {
        ...bbSanitizeLineProperties(item.properties || {}),
        _bundle_id: bundleId,
        _bundle_size: pendingItems.length,
        _bundle_index: idx + 1,
        Bundle: bundleLabel
      }
    }));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || data.message || `Cart error (${response.status})`);
    }

    const result = await response.json();

    const addedLines = Array.isArray(result.items) ? result.items : [];
    for (let i = 0; i < pendingItems.length; i++) {
      const row = addedLines[i];
      if (row && row.key) {
        pendingItems[i].cartLineKey = row.key;
        pendingItems[i].syncedToCart = true;
      }
    }
    await BundleCart.fillBundleLineKeysFromCart(bundleId, pendingItems);
    state.touch();

    // Auto-apply the configured discount code, if any.
    if (discountCode) {
      try {
        await BundleCart.applyDiscountCode(discountCode);
      } catch (e) {
        console.warn('[BundleBuilder] Could not auto-apply discount code:', e);
      }
    }

    return result;
  }

  static async fetchCartJson() {
    const res = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`Cart fetch failed (${res.status})`);
    return res.json();
  }

  /**
   * Shopify line item keys from `/cart.js` → `cart/change` with qty 0.
   */
  static async removeLinesByKeys(keys) {
    const uniq = [...new Set((keys || []).filter(Boolean))];
    for (const id of uniq) {
      try {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id, quantity: 0 }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          console.warn(
            '[BundleBuilder] cart/change failed for line',
            id,
            err.description || err.message || r.status
          );
        }
      } catch (e) {
        console.warn('[BundleBuilder] cart/change network error:', e);
      }
    }
  }

  /** When add.js returns fewer rows than requested, resolve keys via `_bundle_index`. */
  static async fillBundleLineKeysFromCart(bundleId, pendingItems) {
    if (!bundleId || !pendingItems.some(it => !it.cartLineKey)) return;
    let cart;
    try {
      cart = await BundleCart.fetchCartJson();
    } catch (_) {
      return;
    }
    const items = cart.items || [];
    for (let i = 0; i < pendingItems.length; i++) {
      if (pendingItems[i].cartLineKey) continue;
      const found = items.find(ci => {
        const p = ci.properties || {};
        return (
          String(p._bundle_id || '') === String(bundleId) &&
          Number(p._bundle_index) === i + 1
        );
      });
      if (found && found.key) {
        pendingItems[i].cartLineKey = found.key;
        pendingItems[i].syncedToCart = true;
      }
    }
  }

  static async attachKeysWithRetries(state, bundleLineKeys, opts) {
    const tries = (opts && opts.tries) || 12;
    const delayMs = (opts && opts.delayMs) || 120;
    for (let t = 0; t < tries; t++) {
      try {
        const cart = await BundleCart.fetchCartJson();
        const pool = (cart.items || []).slice().reverse();
        for (const lk of bundleLineKeys) {
          const bun = state.items.find(i => String(i.lineKey) === String(lk));
          if (!bun || bun.cartLineKey) continue;
          const idx = pool.findIndex(
            ci => Number(ci.variant_id) === Number(bun.variantId)
          );
          if (idx < 0) continue;
          bun.cartLineKey = pool[idx].key;
          bun.syncedToCart = true;
          pool.splice(idx, 1);
        }
      } catch (_) {}
      const stillMissing = bundleLineKeys.some(lk => {
        const b = state.items.find(i => String(i.lineKey) === String(lk));
        return b && !b.cartLineKey;
      });
      if (!stillMissing) break;
      await new Promise(r => setTimeout(r, delayMs));
    }
    state.touch();
  }

  /**
   * Apply a discount code to the cart.
   * Shopify exposes /discount/<CODE> which sets a cookie that auto-applies
   * the discount when the customer hits checkout.
   * We fetch it as a no-op redirect so the cookie is set without navigating.
   */
  static async applyDiscountCode(code) {
    const url = `/discount/${encodeURIComponent(code)}?redirect=/cart.js`;
    const r = await fetch(url, { method: 'GET', credentials: 'same-origin' });
    if (!r.ok && r.status !== 302) {
      throw new Error(`Discount code "${code}" could not be applied`);
    }
    return true;
  }
}

/* ──────────────────────────────────────────────
   BundlePersonalize
   Shopify blocks storefront pages from being framed
   (X-Frame-Options: DENY + frame-ancestors 'none'), so we
   instead fetch the product page HTML and mount the Product
   Personalizer (Zepto) app block directly into the side panel.

   How it works:
     1. fetch(productUrl) → get the full PDP HTML
     2. Pull out:
          - the .product-personalizer mount div (#pplr-{id})
          - the inline <script> that sets pplr_product, pplr_variant_product,
            pplr_inv_product, pplr_shop_currency, etc.
          - the deferred pplr_common.js loader script
     3. Inject the mount div into our side panel
     4. Re-execute the inline scripts in order so the Zepto canvas script
        loads + renders its UI into our injected mount
     5. Observe /cart/add and /cart/change from the personalizer: mirror
        lines into the bundle tray, let the real requests hit Shopify cart
        (PDP-parity). For fetch(), bundle cleanup runs before the forwarded
        /cart/add so cart rows stay aligned. Shopify line `key`s are saved
        on bundle rows where possible so “Remove” in the tray can remove
        the matching cart lines.
────────────────────────────────────────────── */

class BundlePersonalize {
  constructor(root, config, hooks) {
    this.root = root;
    this.config = config;
    this.hooks = hooks || {};

    this.drawer = bbQuery('[data-bb-personalize]', root);
    if (!this.drawer) return;

    this.titleEl    = bbQuery('[data-bb-personalize-title]', this.drawer);
    this.loaderEl   = bbQuery('[data-bb-personalize-loader]', this.drawer);
    this.mountEl    = bbQuery('[data-bb-personalize-mount]', this.drawer);
    this.errorEl    = bbQuery('[data-bb-personalize-error]', this.drawer);
    this.fallbackEl = bbQuery('[data-bb-personalize-fallback]', this.drawer);
    this.statusEl   = bbQuery('[data-bb-personalize-status]', this.drawer);

    this._activeProductId = null;
    this._activeProductHandle = null;
    this._activeProductUrl = null;
    this._cartHookInstalled = false;
    this._loadToken = 0;
    /** Zepto sometimes fires fetch + XHR for one click; avoid double lines. */
    this._lastInterceptSig = '';
    this._lastInterceptAt = 0;
    this._closeAfterChainTimer = null;

    this._bindClose();
    this._installCartHook();
  }

  _bindClose() {
    bbQueryAll('[data-bb-personalize-close]', this.drawer).forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        this.close();
      });
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  /**
   * Zepto can fire a short request chain (/cart/add then /cart/change).
   * Keep intercepting for a brief quiet period before closing the panel.
   */
  _scheduleCloseAfterInterceptChain(delayMs = 800) {
    clearTimeout(this._closeAfterChainTimer);
    this._closeAfterChainTimer = setTimeout(() => {
      this.close();
    }, delayMs);
  }

  /**
   * Patch window.fetch, XMLHttpRequest and form submissions so every
   * personalizer cart request is mirrored into bundle state while still
   * flowing to Shopify cart normally (PDP-parity behavior).
   */
  _installCartHook() {
    if (this._cartHookInstalled) return;
    this._cartHookInstalled = true;

    const self = this;
    const isCartAddUrl = (url) =>
      !!url && /\/cart\/add(\.js|\.json)?(\?|$)/i.test(String(url));
    const isCartChangeUrl = (url) =>
      !!url && /\/cart\/(change|update)(\.js|\.json)?(\?|$)/i.test(String(url));
    const shouldIntercept = () => self.drawer.classList.contains('is-open');

    const origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function patchedFetch(input, init) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (shouldIntercept() && isCartAddUrl(url)) {
          return self._mirrorCartAddThenFetch(input, init, origFetch, this, arguments);
        }
        if (shouldIntercept() && isCartChangeUrl(url)) {
          return self._mirrorCartChangeThenFetch(input, init, origFetch, this, arguments);
        }
        return origFetch.apply(this, arguments);
      };
    }

    const OrigXhr = window.XMLHttpRequest;
    if (OrigXhr && OrigXhr.prototype && OrigXhr.prototype.open) {
      const origOpen = OrigXhr.prototype.open;
      const origSend = OrigXhr.prototype.send;
      const origSetHeader = OrigXhr.prototype.setRequestHeader;

      OrigXhr.prototype.open = function (method, url) {
        this.__bbUrl = url;
        this.__bbMethod = method;
        this.__bbHeaders = {};
        return origOpen.apply(this, arguments);
      };
      OrigXhr.prototype.setRequestHeader = function (name, value) {
        if (this.__bbHeaders) this.__bbHeaders[String(name).toLowerCase()] = value;
        return origSetHeader.apply(this, arguments);
      };
      OrigXhr.prototype.send = function (body) {
        if (shouldIntercept() && isCartAddUrl(this.__bbUrl)) {
          self._captureXhrAdd(this, body);
          return origSend.apply(this, arguments);
        }
        if (shouldIntercept() && isCartChangeUrl(this.__bbUrl)) {
          self._captureXhrChange(this, body);
          return origSend.apply(this, arguments);
        }
        return origSend.apply(this, arguments);
      };
    }

    document.addEventListener('submit', function (e) {
      if (!shouldIntercept()) return;
      const form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = (form.getAttribute('action') || '').toLowerCase();
      if (!isCartAddUrl(action)) return;
      self._handleFormSubmit(form);
    }, true);
  }

  /** Mirror personalize add into bundle (and Shopify cart removals) BEFORE the real /cart/add runs. */
  async _mirrorCartAddThenFetch(input, init, origFetch, fetchThis, fetchArgs) {
    try {
      const body = await this._readFetchBody(input, init);
      const inputReq = input instanceof Request ? input : null;
      const payload = this._parseAddBody(body, init, inputReq);
      const items = this._normaliseItems(payload);
      const filled = this._fillMissingVariantId(items);
      const withIds = filled.filter(it => Number(it.id));
      if (withIds.length && !this._shouldSkipDuplicateIntercept(withIds)) {
        await this._routeItemsToBundle(withIds);
      }
    } catch (e) {
      console.warn('[BundleBuilder] Failed to mirror personalize add:', e);
    } finally {
      this._scheduleCloseAfterInterceptChain();
    }
    return origFetch.apply(fetchThis, fetchArgs);
  }

  async _mirrorCartChangeThenFetch(input, init, origFetch, fetchThis, fetchArgs) {
    try {
      const body = await this._readFetchBody(input, init);
      const inputReq = input instanceof Request ? input : null;
      const payload = this._parseAddBody(body, init, inputReq);
      await this._applyChangePayloadProperties(payload);
    } catch (e) {
      console.warn('[BundleBuilder] Failed to mirror personalize change:', e);
    } finally {
      this._scheduleCloseAfterInterceptChain();
    }
    return origFetch.apply(fetchThis, fetchArgs);
  }

  _captureXhrAdd(xhr, body) {
    const self = this;
    (async () => {
      try {
        const headers = xhr.__bbHeaders || {};
        const contentType = headers['content-type'] || '';
        const payload = self._parseAddBody(body, {
          headers: { 'Content-Type': contentType },
        });
        const items = self._normaliseItems(payload);
        const filled = self._fillMissingVariantId(items);
        const withIds = filled.filter(it => Number(it.id));
        if (withIds.length && !self._shouldSkipDuplicateIntercept(withIds)) {
          await self._routeItemsToBundle(withIds);
        }
      } catch (e) {
        console.warn('[BundleBuilder] Failed to mirror personalize add (xhr):', e);
      } finally {
        self._scheduleCloseAfterInterceptChain();
      }
    })();
  }

  _captureXhrChange(xhr, body) {
    const self = this;
    (async () => {
      try {
        const headers = xhr.__bbHeaders || {};
        const contentType = headers['content-type'] || '';
        const payload = self._parseAddBody(body, {
          headers: { 'Content-Type': contentType },
        });
        await self._applyChangePayloadProperties(payload);
      } catch (e) {
        console.warn('[BundleBuilder] Failed to mirror personalize change (xhr):', e);
      } finally {
        self._scheduleCloseAfterInterceptChain();
      }
    })();
  }

  _shouldSkipDuplicateIntercept(items) {
    const sig = JSON.stringify(items);
    const now = Date.now();
    if (sig === this._lastInterceptSig && now - this._lastInterceptAt < 450) {
      return true;
    }
    this._lastInterceptSig = sig;
    this._lastInterceptAt = now;
    return false;
  }

  /**
   * <form action="/cart/add"> submit interceptor.
   */
  _handleFormSubmit(form) {
    const self = this;
    (async () => {
      try {
        const fd = new FormData(form);
        const payload = self._formDataToObject(fd);
        const items = self._normaliseItems(payload);
        const filled = self._fillMissingVariantId(items);
        const withIds = filled.filter(it => Number(it.id));
        if (withIds.length && !self._shouldSkipDuplicateIntercept(withIds)) {
          await self._routeItemsToBundle(withIds);
        }
      } catch (e) {
        console.warn('[BundleBuilder] Failed to mirror personalize form add:', e);
      } finally {
        self._scheduleCloseAfterInterceptChain();
      }
    })();
  }

  /**
   * If Zepto's intercepted payload doesn't include a variant `id` (e.g.
   * the form Zepto submitted didn't have one for some reason), fall back
   * to the variant id we captured when the user clicked Personalize.
   */
  _fillMissingVariantId(items) {
    if (!Array.isArray(items)) return items;
    const fallbackId = this._activeVariantId
      ? Number(this._activeVariantId)
      : null;
    if (!fallbackId) return items;
    let usedFallback = false;
    return items.map(it => {
      const next = { ...it };
      if (!next.id && !usedFallback) {
        next.id = fallbackId;
        usedFallback = true;
      }
      return next;
    });
  }

  /**
   * Read the body of a fetch() call regardless of input type.
   * Zepto often uses `fetch(new Request(url, { body: formData }))` with no
   * second arg — reading that Request as .text() destroys all fields.
   */
  async _readFetchBody(input, init) {
    if (init && typeof init.body !== 'undefined') return init.body;
    if (input instanceof Request) {
      const ctInit =
        init && init.headers
          ? typeof init.headers.get === 'function'
            ? init.headers.get('content-type') || ''
            : init.headers['content-type'] || init.headers['Content-Type'] || ''
          : '';
      const ctReq = input.headers.get('content-type') || '';
      const ct = String(ctInit || ctReq).toLowerCase();
      if (ct.includes('multipart/form-data')) {
        try {
          return await input.clone().formData();
        } catch (_) {}
      }
      if (ct.includes('application/x-www-form-urlencoded')) {
        try {
          const t = await input.clone().text();
          return new URLSearchParams(t);
        } catch (_) {
          return null;
        }
      }
      try {
        return await input.clone().text();
      } catch (_) {
        return null;
      }
    }
    return null;
  }

  /**
   * Parse a /cart/add request body into a plain JS object. Body can be:
   *   - JSON string ({"id":..., "quantity":..., "properties":{...}})
   *   - URL-encoded form (FormData / URLSearchParams string)
   *   - FormData
   */
  _parseAddBody(body, init, inputRequest) {
    if (!body) return {};
    const headers = (init && init.headers) || {};
    let contentType = (
      (headers.get && headers.get('content-type')) ||
      headers['Content-Type'] ||
      headers['content-type'] ||
      ''
    ).toLowerCase();
    if (!contentType && inputRequest && inputRequest.headers) {
      contentType = (inputRequest.headers.get('content-type') || '').toLowerCase();
    }

    if (body instanceof FormData) return this._formDataToObject(body);
    if (body instanceof URLSearchParams) {
      return this._formDataToObject(body);
    }
    if (typeof body !== 'string') {
      try { return body; } catch (_) { return {}; }
    }

    if (contentType.indexOf('application/json') !== -1) {
      try { return JSON.parse(body); } catch (_) {}
    }
    if (contentType.indexOf('application/x-www-form-urlencoded') !== -1) {
      return this._formDataToObject(new URLSearchParams(body));
    }
    try { return JSON.parse(body); } catch (_) {}
    try { return this._formDataToObject(new URLSearchParams(body)); } catch (_) {}
    return {};
  }

  /**
   * Convert a FormData / URLSearchParams into a nested object with
   * `properties[Foo]=bar` parsed as { properties: { Foo: 'bar' } }.
   */
  _formDataToObject(fd) {
    const out = {};
    const itemBuckets = {};

    fd.forEach((value, key) => {
      const itemsM = key.match(/^items\[(\d+)\]\[([^\]]+)\](?:\[([^\]]+)\])?$/);
      if (itemsM) {
        const idx = itemsM[1];
        const field = itemsM[2];
        const propChild = itemsM[3];
        if (!itemBuckets[idx]) itemBuckets[idx] = {};
        if (field === 'properties' && propChild) {
          if (!itemBuckets[idx].properties || typeof itemBuckets[idx].properties !== 'object') {
            itemBuckets[idx].properties = {};
          }
          itemBuckets[idx].properties[propChild] = value;
        } else if (!propChild) {
          itemBuckets[idx][field] = value;
        }
        return;
      }

      const m = key.match(/^([^\[]+)\[([^\]]+)\]$/);
      if (m) {
        const parent = m[1];
        const child = m[2];
        if (!out[parent] || typeof out[parent] !== 'object' || Array.isArray(out[parent])) {
          out[parent] = {};
        }
        out[parent][child] = value;
      } else if (out[key] !== undefined) {
        if (!Array.isArray(out[key])) out[key] = [out[key]];
        out[key].push(value);
      } else {
        out[key] = value;
      }
    });

    const bucketKeys = Object.keys(itemBuckets).sort(
      (a, b) => Number(a) - Number(b)
    );
    if (bucketKeys.length) {
      out.items = bucketKeys.map(k => {
        const b = itemBuckets[k];
        const rawId = b.id != null ? b.id : null;
        const id = rawId != null ? Number(rawId) || rawId : null;
        const line = {
          id,
          quantity: Math.max(1, Number(b.quantity) || 1),
        };
        const propsMerged = bbExtractLineProperties(b.properties);
        if (propsMerged) line.properties = propsMerged;
        if (b.product_id != null) line.product_id = Number(b.product_id) || b.product_id;
        return line;
      });
    }

    return out;
  }

  /**
   * Zepto’s `jQuery.ajax`/XHR path can bypass our intercepted body snapshot in
   * edge cases — read the exact same `[name="properties[Label]"]` DOM fields
   * Zepto submits (modal `.pplr_crop-modal`, drawer mount, `.pplrform`).
   */
  _domPropertiesFromPersonalizerForms() {
    const selectors = [
      '.pplr_crop-modal form.pplr_atc_form',
      '.pplr_crop-modal form.pplr_form',
      '.pplr_crop-modal form[action*="/cart/add"]',
      'form.pplr_atc_form[action*="cart/add"]',
      'form.pplrform',
      'form.bb-personalize__host-form',
    ];
    const seen = new Set();
    /** @type {HTMLFormElement[]} */
    const forms = [];

    function collect(root) {
      if (!root || !root.querySelectorAll) return;
      for (const sel of selectors) {
        root.querySelectorAll(sel).forEach(el => {
          if (!(el instanceof HTMLFormElement)) return;
          if (seen.has(el)) return;
          seen.add(el);
          forms.push(el);
        });
      }
    }

    collect(document.body);
    collect(this.mountEl);
    collect(this.drawer);

    /** Pick the richest property set Zepto assembled (typically the modal PDP form). */
    let bestProps = null;
    let bestScore = -1;
    for (const form of forms) {
      try {
        const inspected = bbInspectFormPropertyPairs(form);
        let props = inspected;

        try {
          const fd = new FormData(form);
          const obj = this._formDataToObject(fd);
          const fromFd =
            bbExtractLineProperties(obj.properties) ||
            (Array.isArray(obj.items)
              ? bbExtractLineProperties(
                  obj.items.find(
                    it =>
                      this._activeVariantId &&
                      Number(it.id) === Number(this._activeVariantId)
                  )?.properties
                )
              : null);
          if (fromFd && Object.keys(fromFd).length) {
            props = props ? Object.assign({}, fromFd, props) : fromFd;
          }
        } catch (_) {}

        if (!props || !Object.keys(props).length) continue;
        const n = Object.keys(props).length;
        if (n > bestScore) {
          bestScore = n;
          bestProps = props;
        }
      } catch (_) {}
    }

    return bestProps;
  }

  /**
   * Normalise the parsed body into an array of {id, quantity, properties}
   * line items. /cart/add.js supports both single and bulk forms.
   */
  _normaliseItems(payload) {
    if (!payload) return [];
    if (Array.isArray(payload.items) && payload.items.length) {
      return payload.items.map(it => {
        const id = it.id ?? it.variant_id ?? it.variantId ?? null;
        const props = bbExtractLineProperties(it.properties);
        return {
          ...it,
          id,
          quantity: Math.max(1, Number(it.quantity) || 1),
          properties: props,
        };
      });
    }
    const id = payload.id || payload.variant_id || payload.variantId || null;
    const props = bbExtractLineProperties(payload.properties);
    return [{
      id,
      quantity: Number(payload.quantity) || 1,
      properties: props,
    }];
  }

  /**
   * Parse /cart/change payload and patch saved properties for the active
   * parent variant. Zepto commonly uses change.js to finalize line attrs.
   */
  async _applyChangePayloadProperties(payload) {
    if (!payload || typeof this.hooks.onPersonalizedPropertiesPatch !== 'function') {
      return;
    }
    const props =
      bbExtractLineProperties(payload.properties) ||
      bbExtractLineProperties(payload.newproperties) ||
      null;
    if (!props || !Object.keys(props).length) return;
    const parentVariantId = this._activeVariantId ? Number(this._activeVariantId) : null;
    if (!parentVariantId) return;
    await this.hooks.onPersonalizedPropertiesPatch({
      parentVariantId,
      parentProductId: this._activeProductId,
      properties: props,
    });
  }

  /**
   * Forward each captured line to the bundle via the orchestrator hook.
   */
  async _routeItemsToBundle(items) {
    if (!items || !items.length) return;
    /**
     * Zepto often fires TWO /cart/add calls: first the physical product
     * (with line properties), then the “Item Personalization” fee variant.
     * Cleanup (remove plain bundle row + prior pp lines) must run ONLY
     * when this payload actually contains that main variant — otherwise
     * the fee-only pass would delete the main line we just stored.
     */
    const parentVid = this._activeVariantId
      ? Number(this._activeVariantId)
      : null;
    const touchesMainProduct =
      parentVid != null &&
      !Number.isNaN(parentVid) &&
      items.some(it => Number(it.id) === parentVid);
    if (
      touchesMainProduct &&
      typeof this.hooks.onPersonalizedBatchStart === 'function'
    ) {
      await this.hooks.onPersonalizedBatchStart({
        personalizationParentVariantId: this._activeVariantId,
        personalizationParentProductId: this._activeProductId,
      });
    }
    if (typeof this.hooks.onPersonalized !== 'function') return;

    /** One DOM read per intercepted batch (cheap vs missing monogram props on checkout). */
    let domPropSnapshot = null;
    if (
      parentVid != null &&
      items.some(it => Number(it.id) === parentVid)
    ) {
      domPropSnapshot = this._domPropertiesFromPersonalizerForms();
    }

    for (const it of items) {
      const variantId = Number(it.id);
      if (!variantId) continue;
      const quantity = Math.max(1, Number(it.quantity) || 1);
      let properties = bbExtractLineProperties(it.properties);
      if (
        parentVid != null &&
        variantId === parentVid &&
        domPropSnapshot &&
        Object.keys(domPropSnapshot).length
      ) {
        properties = Object.assign({}, domPropSnapshot, properties || {});
      }
      const rowProductId =
        it.product_id != null
          ? Number(it.product_id)
          : it.productId != null
            ? Number(it.productId)
            : null;
      const productId =
        rowProductId != null && !Number.isNaN(rowProductId)
          ? rowProductId
          : this._activeProductId;
      await this.hooks.onPersonalized({
        variantId,
        quantity,
        properties,
        productId,
        productHandle: this._activeProductHandle,
        personalizationParentProductId: this._activeProductId,
        personalizationParentVariantId: this._activeVariantId,
      });
    }
  }

  /**
   * Open the panel for a given product. Fetches the PDP HTML and mounts
   * the Product Personalizer block into the panel.
   */
  async open({ productUrl, productTitle, productId, productHandle, variantId }) {
    if (!this.drawer) return;

    this._activeProductId = productId ? Number(productId) : null;
    this._activeProductHandle = productHandle || null;
    this._activeProductUrl = productUrl || '';
    this._activeVariantId = variantId ? Number(variantId) : null;

    if (this.titleEl) {
      this.titleEl.textContent = productTitle || 'Product';
    }
    if (this.fallbackEl) {
      this.fallbackEl.href = productUrl || '#';
    }

    this._showLoader();
    this._hideError();
    this._hideStatus();
    if (this.mountEl) this.mountEl.innerHTML = '';

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bb-personalize-open');

    const token = ++this._loadToken;

    try {
      await this._loadAndMount(productUrl);
      if (token !== this._loadToken) return;
      this._hideLoader();
    } catch (err) {
      if (token !== this._loadToken) return;
      console.warn('[BundleBuilder] Could not mount personalizer:', err);
      this._hideLoader();
      this._showError();
    }
  }

  close() {
    if (!this.drawer) return;
    clearTimeout(this._closeAfterChainTimer);
    this._loadToken++;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bb-personalize-open');

    this._hostForm = null;

    if (this.mountEl) this.mountEl.innerHTML = '';
    this._closeZeptoCustomizerUi();

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  /**
   * Zepto's full-screen customize modal is closed via global `pplr_w_h`
   * (same handler as the X on the modal). When we intercept /cart/add
   * we must invoke that and/or strip `.pplr_crop-modal` / overlay nodes,
   * otherwise the popup stays up with a loading spinner.
   */
  _closeZeptoCustomizerUi() {
    try {
      if (typeof window.pplr_w_h === 'function') window.pplr_w_h();
    } catch (_) {}
    this._cleanupZeptoOverlays();
    const self = this;
    setTimeout(() => self._cleanupZeptoOverlays(), 60);
    setTimeout(() => self._cleanupZeptoOverlays(), 520);
  }

  /**
   * Some personalizer apps (Zepto / Inkybay) attach modals/overlays
   * directly to <body>. When we close our panel, hide/remove any of
   * those that are left visible so they don't stick around.
   */
  _cleanupZeptoOverlays() {
    const selectors = [
      '#pplr_modal',
      '[id^="pplr_modal"]',
      '[id^="pplr-modal"]',
      '.pplr-modal',
      '.pplr-modal-wrap',
      '.pplr_crop-modal',
      '.pplr-modal-overlay',
      '.pplr_overlay',
      '#pplr_overlay',
      '.pplr_popup',
      '.pplr-popup',
      '.pplr-modal-box',
      '.pplr-customizer',
      '.pplr_customizer',
    ];
    selectors.forEach(sel => {
      document.querySelectorAll(sel).forEach(el => {
        if (this.drawer && this.drawer.contains(el)) return;
        try { el.remove(); } catch (_) { el.style.display = 'none'; }
      });
    });
    document.body.classList.remove(
      'pplr-modal-open',
      'pplr_open',
      'pplr_fixed'
    );
  }

  _showLoader()  { if (this.loaderEl) this.loaderEl.classList.remove('is-hidden'); }
  _hideLoader()  { if (this.loaderEl) this.loaderEl.classList.add('is-hidden'); }
  _showError()   { if (this.errorEl)  this.errorEl.removeAttribute('hidden'); }
  _hideError()   { if (this.errorEl)  this.errorEl.setAttribute('hidden', ''); }
  _showStatus()  { if (this.statusEl) this.statusEl.removeAttribute('hidden'); }
  _hideStatus()  { if (this.statusEl) this.statusEl.setAttribute('hidden', ''); }

  /**
   * Fetch the product page HTML and mount the Product Personalizer block
   * (mount node + inline init scripts + deferred loader script) into the
   * side panel.
   *
   * We build a VISIBLE host form right inside the mount area and put the
   * personalizer mount node + its scripts INSIDE that form. The Zepto app
   * always tries to relocate `.product-personalizer` into the page's
   * product form (`_P_P_D.prependTo(form)`), and if our form is the only
   * one on the page Zepto uses it. By having the form already wrap the
   * mount, the personalizer stays exactly where we placed it (visible)
   * instead of being moved off-screen, AND Zepto can submit the form
   * normally on Add-to-Cart so we capture variant + properties.
   */
  async _loadAndMount(productUrl) {
    if (!productUrl) throw new Error('No product URL');
    if (!this.mountEl) throw new Error('No mount element');

    const res = await fetch(productUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!res.ok) throw new Error(`PDP fetch failed: ${res.status}`);
    const html = await res.text();

    const doc = new DOMParser().parseFromString(html, 'text/html');

    const personalizerMount = doc.querySelector('div.product-personalizer[id^="pplr-"]');
    if (!personalizerMount) {
      throw new Error('No product personalizer block on PDP');
    }

    const blockNodes = this._collectPersonalizerBlock(personalizerMount);
    if (!blockNodes.length) {
      throw new Error('Could not collect personalizer block nodes');
    }

    this._resetPersonalizerGlobals();

    const hostForm = this._buildHostProductForm(doc);
    this.mountEl.appendChild(hostForm);
    this._hostForm = hostForm;

    blockNodes.forEach(node => {
      if (node.tagName === 'SCRIPT') {
        hostForm.appendChild(this._cloneExecutableScript(node));
      } else {
        hostForm.appendChild(node.cloneNode(true));
      }
    });
  }

  /**
   * Reset the Zepto Product Personalizer's globals so re-mounting it for
   * a different product (or the same product re-opened after close)
   * causes the inline init script to re-load and re-render. The PDP's
   * init script gates on `typeof pplr_script_loaded == "undefined"`, so
   * we forcibly set it to undefined (also a few related globals) so the
   * init logic re-runs end-to-end and `loadScript_p(canvas-script.php)`
   * fires again — which is what actually renders the Personalize button.
   *
   * Note: `var`-declared globals can't be `delete`-d in non-strict mode,
   * so we assign undefined instead.
   */
  _resetPersonalizerGlobals() {
    const keys = [
      'pplr_script_loaded',
      'product_personalizer_loaded',
      'pplr_Loaded',
      'pplr_atc_form_id',
      'pplr_form_id',
      'pplr_on_boostly',
      'pplr_is_popup',
      'pplr_product',
      'pplr_variant_product',
      'pplr_inv_product',
      'product_personalizer',
      'pplr_cart',
      'loadScript_p',
      'loadScript_pplr',
      'pd_id',
      'pplr_prod_id',
      'fontgroup_json',
      'colorgroup_json',
      'imagegroup_json',
      'dropdown_json',
      'settings_json',
      'pplrvariantid',
      'pricechanger',
      'vpriceid',
      '_SJ2',
      '_CP',
      '_PP',
      'conditions_json',
      'pplr_values',
      'pplr_values_origin',
      'pplr_views',
      'pplr_is_views',
      'pplr_total_views',
    ];
    keys.forEach(k => {
      try { window[k] = undefined; } catch (_) {}
      try { delete window[k]; } catch (_) {}
    });
  }

  /**
   * Build a visible, in-flow product form. The personalizer mount + its
   * scripts will be inserted INSIDE this form so that when Zepto runs:
   *   _P_P_D.prependTo( jQuery('form[action*="/cart/add"]') )
   * the personalizer stays inside this same form (which is in our visible
   * panel) — not relocated off-screen.
   *
   * Zepto looks for forms matching:
   *   form[id]:has([type="submit"]):visible [name="id"]
   * so our form needs an `id`, a submit button (we keep it tiny + hidden
   * via CSS but kept in the DOM), and a `name="id"` hidden input with the
   * MAIN variant id.
   */
  _buildHostProductForm(pdpDoc) {
    const variantId = this._activeVariantId
      ? String(this._activeVariantId)
      : (this._extractDefaultVariantId(pdpDoc) || '');

    const form = document.createElement('form');
    form.className = 'bb-personalize__host-form';
    form.setAttribute('method', 'post');
    form.setAttribute('action', '/cart/add');
    form.setAttribute('enctype', 'multipart/form-data');
    form.setAttribute('accept-charset', 'UTF-8');
    form.setAttribute('novalidate', 'novalidate');
    form.setAttribute('data-product-form', '');
    form.id = 'bb-personalize-host-form';

    if (variantId) {
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'id';
      idInput.value = variantId;
      form.appendChild(idInput);
    }

    const qtyInput = document.createElement('input');
    qtyInput.type = 'hidden';
    qtyInput.name = 'quantity';
    qtyInput.value = '1';
    form.appendChild(qtyInput);

    const ft = document.createElement('input');
    ft.type = 'hidden';
    ft.name = 'form_type';
    ft.value = 'product';
    form.appendChild(ft);

    // Zepto's selector requires `:has([type="submit"])` — keep a real
    // submit button in the form but hide it visually. We also intercept
    // the form's submit globally so this button never actually navigates.
    const fakeSubmit = document.createElement('button');
    fakeSubmit.type = 'submit';
    fakeSubmit.name = 'add';
    fakeSubmit.className = 'bb-personalize__host-submit';
    fakeSubmit.setAttribute('aria-hidden', 'true');
    fakeSubmit.tabIndex = -1;
    fakeSubmit.style.cssText =
      'position:absolute;left:-99999px;top:-99999px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
    fakeSubmit.textContent = 'Add';
    form.appendChild(fakeSubmit);

    return form;
  }

  /**
   * Pull the default-selected variant id out of the PDP HTML, so we can
   * fall back to it if the bundle card didn't pass us one.
   */
  _extractDefaultVariantId(doc) {
    if (!doc) return '';
    const idInput = doc.querySelector(
      'form[action*="/cart/add"] input[name="id"]'
    );
    if (idInput && idInput.value) return idInput.value;
    const variantInput = doc.querySelector(
      'form[action*="/cart/add"] [name="id"]:checked'
    );
    if (variantInput && variantInput.value) return variantInput.value;
    return '';
  }

  /**
   * Collect the personalizer mount element + every following sibling that
   * belongs to the same Zepto block (its inline config <script>, the
   * <style>, and the deferred loader <script>) until we hit another
   * unrelated element.
   */
  _collectPersonalizerBlock(mountNode) {
    const out = [mountNode];

    let cur = mountNode.nextElementSibling;
    let scriptCount = 0;
    while (cur) {
      const tag = cur.tagName;

      if (tag === 'SCRIPT') {
        const code = cur.textContent || '';
        const src = cur.getAttribute('src') || '';
        const isPplr =
          /pplr|product_personalizer|cdn-zeptoapps|zepto/i.test(code) ||
          /pplr|cdn-zeptoapps|zepto/i.test(src);
        if (isPplr) {
          out.push(cur);
          scriptCount++;
          cur = cur.nextElementSibling;
          if (scriptCount >= 4) break;
          continue;
        }
        break;
      }

      if (tag === 'STYLE') {
        const code = cur.textContent || '';
        if (/pplr|product-personalizer/i.test(code)) {
          out.push(cur);
          cur = cur.nextElementSibling;
          continue;
        }
        break;
      }

      break;
    }

    return out;
  }

  /**
   * Recreating a <script> via cloneNode won't execute. We need a brand-new
   * script element with the same attrs and code for the browser to evaluate.
   */
  _cloneExecutableScript(scriptNode) {
    const s = document.createElement('script');
    for (const attr of scriptNode.attributes) {
      s.setAttribute(attr.name, attr.value);
    }
    if (scriptNode.textContent) {
      s.textContent = scriptNode.textContent;
    }
    return s;
  }

}

/* ──────────────────────────────────────────────
   BundleBuilder — orchestrator
────────────────────────────────────────────── */

class BundleBuilder {
  constructor(root, config) {
    this.root = root;
    this.config = config;

    this.state = new BundleState({
      minItems: config.minItems,
      maxItems: config.maxItems,
      discountPct: config.discountPct
    });

    this.state.onChange(() => {
      bbBundleSessionSave(config.sectionId, this.state.serializeForSession());
    });

    const saved = bbBundleSessionLoad(config.sectionId);
    if (saved && saved.length) {
      this.state.hydrateFromSession(saved);
    }

    this.tray = new BundleTray(root, this.state, config);

    if (config.enablePersonalize) {
      this.personalize = new BundlePersonalize(root, config, {
        onPersonalizedBatchStart: (ctx) =>
          this._onPersonalizedBatchStart(ctx),
        onPersonalizedPropertiesPatch: (ctx) =>
          this._onPersonalizedPropertiesPatch(ctx),
        onPersonalized: (payload) => this._onPersonalizedAdd(payload),
      });
    }

    this._bindCards();
    this._bindTrayEvents();
    this._bindCTA();
    this._bindPersonalizeButtons();

    // Initial render
    this.tray.update(this.state);
    this._restoreCardSelectionFromState();
    this._syncPersonalizeButtons();
  }

  /** Removing a bundle row also drops matching lines from Shopify cart when tracked. */
  async _syncShopifyCartRemove(cartKeys) {
    const uniq = [...new Set((cartKeys || []).filter(Boolean))];
    if (!uniq.length) return;
    await BundleCart.removeLinesByKeys(uniq);
    await bbNotifyCartUpdated();
  }

  /** After session hydrate, sync grid “selected” styling with bundle lines. */
  _restoreCardSelectionFromState() {
    bbQueryAll('.bb-card', this.root).forEach(card => {
      const variantSelect = bbQuery('.bb-card__variant-select', card);
      if (!variantSelect || !variantSelect.value) return;
      const vid = Number(variantSelect.value);
      const pid = card.dataset.productId || null;
      const inBundle = this.state.includesCardVariant(pid, vid);
      card.classList.toggle('is-selected', inBundle);
    });
  }

  /**
   * Personalize mirrors the PDP: open any time there is room (or this card
   * already contributes) and a variant + product URL are known.
   */
  _syncPersonalizeButtons() {
    if (!this.config.enablePersonalize) return;
    bbQueryAll('.bb-card', this.root).forEach(card => {
      const btn = bbQuery('.bb-card__btn--personalize', card);
      if (!btn) return;
      const variantSelect = bbQuery('.bb-card__variant-select', card);
      if (!variantSelect || !variantSelect.value) {
        btn.disabled = true;
        btn.title = 'Select a variant first.';
        return;
      }
      const currentVariantId = Number(variantSelect.value);
      if (!currentVariantId) {
        btn.disabled = true;
        btn.title = 'Select a variant first.';
        return;
      }
      const productUrl =
        btn.dataset.productUrl || (card && card.dataset.productUrl);
      if (!productUrl) {
        btn.disabled = true;
        btn.title = '';
        return;
      }
      const pid = card.dataset.productId || null;
      const contributes = this.state.includesCardVariant(pid, currentVariantId);
      // Hide Personalize once the current variant already contributes
      // to the bundle (standard or personalized path).
      btn.hidden = contributes;
      btn.setAttribute('aria-hidden', contributes ? 'true' : 'false');
      if (contributes) {
        btn.style.display = 'none';
        btn.disabled = true;
        btn.title = '';
        return;
      }
      btn.style.display = '';
      const enabled = !this.state.isMaxed;
      btn.disabled = !enabled;
      const maxHint =
        btn.getAttribute('data-bb-personalize-hint') ||
        btn.dataset.bbPersonalizeHint ||
        '';
      btn.title = enabled ? '' : maxHint || 'Unavailable.';
    });
  }

  /* ── Card interactions ── */
  _bindCards() {
    bbQueryAll('.bb-card', this.root).forEach(card => {
      const productId = card.dataset.productId;
      const variantSelect = bbQuery('.bb-card__variant-select', card);
      const btn = bbQuery('.bb-card__btn', card);
      const priceEl = bbQuery('.bb-card__price', card);

      // Update price display when variant changes
      if (variantSelect && variantSelect.tagName === 'SELECT') {
        variantSelect.addEventListener('change', () => {
          const opt = variantSelect.options[variantSelect.selectedIndex];
          this._updateCardPrice(priceEl, opt);

          // If this card is currently selected, remove old variant, add new one
          const prevVariantId = Number(variantSelect.dataset.prevVariantId || variantSelect.value);
          if (this.state.hasVariant(prevVariantId)) {
            void (async () => {
              await this._syncShopifyCartRemove(this.state.removeAll(prevVariantId));
              card.classList.remove('is-selected');
              this._addFromCard(card, variantSelect, btn);
              this.tray.update(this.state);
              this._syncPersonalizeButtons();
            })();
            variantSelect.dataset.prevVariantId = variantSelect.value;
            return;
          }
          variantSelect.dataset.prevVariantId = variantSelect.value;
          this._syncPersonalizeButtons();
        });
      }

      if (btn) {
        btn.addEventListener('click', () => {
          const currentVariantId = Number(variantSelect ? variantSelect.value : card.dataset.productId);
          const isSelected = this.state.hasVariant(currentVariantId);

          void (async () => {
            if (isSelected) {
              await this._syncShopifyCartRemove(this.state.removeAll(currentVariantId));
              card.classList.remove('is-selected');
            } else {
              const added = this._addFromCard(card, variantSelect, btn);
              if (!added && this.state.isMaxed) {
                this.tray.showToast(`Maximum of ${this.config.maxItems} items reached.`, true);
              }
            }
            this.tray.update(this.state);
            this._syncPersonalizeButtons();
          })();
        });
      }
    });
  }

  _addFromCard(card, variantSelect, btn) {
    const opt = variantSelect && variantSelect.tagName === 'SELECT'
      ? variantSelect.options[variantSelect.selectedIndex]
      : variantSelect; // hidden input

    const variantId  = Number(opt ? opt.value : card.dataset.productId);
    const price      = Number(opt ? (opt.dataset || opt).price : 0) || 0;
    const title      = (opt ? (opt.dataset || opt).productTitle : '') || card.querySelector('.bb-card__title')?.textContent.trim() || '';
    const variantTitle = (opt ? (opt.dataset || opt).title : '') || 'Default Title';
    const image      = (opt ? (opt.dataset || opt).image : '') || '';

    const lineKey = this.state.add({ variantId, price, title, variantTitle, image, productId: card.dataset.productId });
    if (lineKey != null) {
      card.classList.add('is-selected');
      // Pulse animation
      card.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.02)' }, { transform: 'scale(1)' }], { duration: 200 });
    }
    return lineKey;
  }

  _updateCardPrice(priceEl, opt) {
    if (!priceEl || !opt) return;
    const price = Number(opt.dataset.price || 0);
    const comparePrice = Number(opt.dataset.comparePrice || 0);
    const currentEl = priceEl.querySelector('.bb-card__price-current');
    const compareEl = priceEl.querySelector('.bb-card__price-compare');
    if (currentEl) currentEl.textContent = bbFormatMoney(price, this.config.moneyFormat);
    if (compareEl) {
      if (comparePrice > price) {
        compareEl.textContent = bbFormatMoney(comparePrice, this.config.moneyFormat);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  /**
   * Look up the product/variant info for a given variantId by scanning
   * the cards in the grid. Returns null if we can't find it (e.g. the
   * personalized variant isn't in the visible bundle source collection).
   */
  _lookupVariantInfo(variantId, productId, productHandle) {
    const cards = bbQueryAll('.bb-card', this.root);

    let card = null;
    if (productId) {
      card = cards.find(c => Number(c.dataset.productId) === Number(productId)) || null;
    }
    if (!card && productHandle) {
      card = cards.find(c => c.dataset.productHandle === productHandle) || null;
    }

    if (card) {
      const select = bbQuery('.bb-card__variant-select', card);
      if (select) {
        if (select.tagName === 'SELECT') {
          for (const opt of select.options) {
            if (Number(opt.value) === Number(variantId)) {
              return {
                card,
                price: Number(opt.dataset.price) || 0,
                title: opt.dataset.productTitle || (card.dataset.productTitle || ''),
                variantTitle: opt.dataset.title || 'Default Title',
                image: opt.dataset.image || '',
              };
            }
          }
        } else if (Number(select.value) === Number(variantId)) {
          return {
            card,
            price: Number(select.dataset.price) || 0,
            title: select.dataset.productTitle || (card.dataset.productTitle || ''),
            variantTitle: select.dataset.title || 'Default Title',
            image: select.dataset.image || '',
          };
        }
      }
    }
    return { card, price: 0, title: '', variantTitle: 'Default Title', image: '' };
  }

  /**
   * Async fallback: fetch /products/{handle}.js to grab variant data
   * when the variant isn't on a card we can read. Returns a partial
   * info object on success.
   */
  async _lookupVariantViaApi(variantId, productHandle) {
    if (!productHandle) return null;
    try {
      const res = await fetch(`/products/${encodeURIComponent(productHandle)}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      const variant = (data.variants || []).find(v => Number(v.id) === Number(variantId));
      if (!variant) return null;
      return {
        productId: data.id != null ? Number(data.id) : null,
        price: Number(variant.price) || 0,
        title: data.title || '',
        variantTitle: variant.title || 'Default Title',
        image: variant.featured_image
          ? (variant.featured_image.src || variant.featured_image)
          : (data.featured_image || ''),
      };
    } catch (_) {
      return null;
    }
  }

  /**
   * Zepto often adds a second line (e.g. “Item Personalization”) whose
   * variant is not on the bundle card. Try every product handle shown in
   * the bundle grid until /products/{handle}.js contains that variant.
   */
  async _lookupVariantAcrossBundleCards(variantId) {
    const handles = new Set();
    bbQueryAll('.bb-card', this.root).forEach(card => {
      const h = card.dataset.productHandle;
      if (h) handles.add(h);
    });
    for (const handle of handles) {
      const hit = await this._lookupVariantViaApi(variantId, handle);
      if (hit && hit.price) return hit;
    }
    return null;
  }

  /**
   * Runs once per Zepto /cart/add payload before lines are applied: remove
   * any previous personalization rows + the plain “Add to Bundle” row so
   * we never send duplicate variant lines (Shopify would keep the wrong
   * line’s properties in checkout).
   */
  async _onPersonalizedBatchStart({
    personalizationParentVariantId,
    personalizationParentProductId,
  }) {
    const pv =
      personalizationParentVariantId != null
        ? Number(personalizationParentVariantId)
        : null;
    if (pv == null || Number.isNaN(pv)) return;
    const ppRaw =
      personalizationParentProductId != null
        ? Number(personalizationParentProductId)
        : null;
    const pp = ppRaw != null && !Number.isNaN(ppRaw) ? ppRaw : null;
    const cartKeys = [
      ...this.state.removePersonalizedLinesForParent(pv, pp),
      ...this.state.removeStandardLineOnly(pv),
    ];
    await this._syncShopifyCartRemove(cartKeys);
  }

  /** Apply late Zepto /cart/change personalization fields onto main line. */
  async _onPersonalizedPropertiesPatch({
    parentVariantId,
    parentProductId,
    properties,
  }) {
    this.state.patchMainPersonalizedProperties(
      parentVariantId,
      parentProductId,
      properties
    );
    this.tray.update(this.state);
    this._syncPersonalizeButtons();
  }

  /**
   * Called by BundlePersonalize when a /cart/add was intercepted from
   * the personalize panel. Adds the item to the bundle as a personalized
   * line (with the captured properties) and shows a toast.
   */
  async _onPersonalizedAdd({
    variantId,
    quantity,
    properties,
    productId,
    productHandle,
    personalizationParentProductId,
    personalizationParentVariantId,
  }) {
    let info = this._lookupVariantInfo(variantId, productId, productHandle);
    if (!info.price) {
      let apiInfo = await this._lookupVariantViaApi(variantId, productHandle);
      if (!apiInfo || !apiInfo.price) {
        apiInfo = await this._lookupVariantAcrossBundleCards(variantId);
      }
      if (apiInfo) info = { ...info, ...apiInfo };
    }

    const parentPid =
      personalizationParentProductId != null
        ? Number(personalizationParentProductId)
        : productId != null
          ? Number(productId)
          : null;
    const parentVid =
      personalizationParentVariantId != null
        ? Number(personalizationParentVariantId)
        : Number(variantId);

    const safeParentPid =
      parentPid != null && !Number.isNaN(parentPid) ? parentPid : null;
    const safeParentVid =
      parentVid != null && !Number.isNaN(parentVid) ? parentVid : null;

    const qty = Math.max(1, Number(quantity) || 1);
    let lastAdded = false;
    /** @type {(string|number)[]} */
    const newBundleLineKeys = [];
    for (let i = 0; i < qty; i++) {
      const lk = this.state.add({
        variantId,
        price: info.price || 0,
        title: info.title || 'Product',
        variantTitle: info.variantTitle || 'Default Title',
        image: info.image || '',
        productId:
          (productId != null && !Number.isNaN(Number(productId))
            ? Number(productId)
            : null) ||
          (info.card && info.card.dataset.productId
            ? Number(info.card.dataset.productId)
            : null) ||
          (info.productId != null && !Number.isNaN(Number(info.productId))
            ? Number(info.productId)
            : null),
        personalized: true,
        syncedToCart: true,
        properties: properties || null,
        personalizationParentProductId: safeParentPid,
        personalizationParentVariantId: safeParentVid,
      });
      if (lk != null) {
        lastAdded = true;
        newBundleLineKeys.push(lk);
      } else {
        this.tray.showToast(
          `Maximum of ${this.config.maxItems} items reached.`,
          true
        );
        break;
      }
    }

    if (lastAdded) {
      this.tray.showToast('Personalized item added to bundle.');
      await BundleCart.attachKeysWithRetries(this.state, newBundleLineKeys);
    }
    this.tray.update(this.state);
    this._restoreCardSelectionFromState();
    this._syncPersonalizeButtons();
  }

  /* ── Personalize buttons (open the personalize side panel) ── */
  _bindPersonalizeButtons() {
    if (!this.personalize) return;
    bbQueryAll('.bb-card__btn--personalize', this.root).forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const card = btn.closest('.bb-card');
        const productUrl =
          btn.dataset.productUrl || (card && card.dataset.productUrl);
        const productTitle =
          btn.dataset.productTitle ||
          (card && card.dataset.productTitle) ||
          '';
        const productId =
          (card && card.dataset.productId) || btn.dataset.productId || null;
        const productHandle =
          (card && card.dataset.productHandle) || null;

        let variantId = null;
        if (card) {
          const select = bbQuery('.bb-card__variant-select', card);
          if (select && select.value) variantId = Number(select.value);
        }

        if (!productUrl) return;
        this.personalize.open({
          productUrl,
          productTitle,
          productId,
          productHandle,
          variantId,
        });
      });
    });
  }

  /* ── Tray remove buttons ── */
  _bindTrayEvents() {
    this.tray.itemsList && this.tray.itemsList.addEventListener('click', e => {
      const removeBtn = e.target.closest('.bb-tray-item__remove');
      if (!removeBtn) return;
      const lineKey = removeBtn.dataset.lineKey;
      void (async () => {
        await this._syncShopifyCartRemove(this.state.removeAll(lineKey));
        this.tray.update(this.state);
        this._restoreCardSelectionFromState();
        this._syncPersonalizeButtons();
      })();
    });
  }

  /* ── CTA ── */
  _bindCTA() {
    const cta = bbQuery('.bb-tray__cta', this.root);
    if (!cta) return;

    cta.addEventListener('click', async () => {
      if (!this.state.isReady) return;

      this.tray.setCtaLoading(true);

      try {
        await BundleCart.addToCart({
          state: this.state,
          discountPct: this.config.discountPct,
          discountCode: this.config.discountCode
        });
        bbBundleSessionClear(this.config.sectionId);
        this.state.clear();
        bbQueryAll('.bb-card.is-selected', this.root).forEach(card => {
          card.classList.remove('is-selected');
        });
        this._syncPersonalizeButtons();

        this.tray.showToast('Bundle added to cart!');

        // Refresh cart UI (count bubble / quick cart) without a page reload
        await bbNotifyCartUpdated();
        // Open cart sidebar after add (theme's open uses an internal event bus;
        // we emulate the DOM state it applies).
        setTimeout(() => bbOpenQuickCart(), 150);

        // Optional: redirect to cart
        // window.location.href = '/cart';
      } catch (err) {
        console.error('[BundleBuilder] Cart error:', err);
        this.tray.showToast(err.message || 'Could not add to cart. Please try again.', true);
      } finally {
        this.tray.setCtaLoading(false);
        this.tray.update(this.state);
      }
    });
  }
}

/* ──────────────────────────────────────────────
   Bootstrap — initialise one instance per section
────────────────────────────────────────────── */

(function () {
  function init() {
    const configs = window.__bb_config || {};
    Object.values(configs).forEach(config => {
      const root = document.getElementById(`bundle-builder-${config.sectionId}`);
      if (!root || root._bbInitialised) return;
      root._bbInitialised = true;
      new BundleBuilder(root, config);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor live reload support
  document.addEventListener('shopify:section:load', init);
})();
